import React from 'react';
import { trackEvent } from '@cx-web/utils/dataLayer';

import { PremiumLock } from '../../primitives/icon/icons';

import styles from './ArticleHeader.module.css';
import { getWebPImageUrlWithWidth } from './getWebPImageUrlWithWidth';

export type ArticleHeaderImage = {
  alt: string;
  url: string;
};

export type ArticleHeaderAuthor = {
  fullName: string;
  id: string;
  image: ArticleHeaderImage;
  shortName: string;
};

export type ArticleHeaderProps = {
  aiGeneratedId?: string;
  authors: ArticleHeaderAuthor[];
  componentTags?: string[];
  description?: string;
  image: ArticleHeaderImage;
  /** Resolved hero image width in pixels. RR7 glue: 400 on mobile, 1000 on desktop. */
  imageWidth: number;
  /** Whether the visitor is logged in. RR7: `!!myocData.myOCUser`; atlas-astro: genericConfig.isLoggedIn. */
  isLoggedIn: boolean;
  /** Open the login modal (premium lock CTA). Wired per app to its auth mechanism. */
  onLogin: () => void;
  /** Localised date string already formatted by the caller (repub timezone + locale). */
  publishedTimeLocalised?: string;
  readTime?: string;
  title: string;
};

// Single source of truth for the page-template ARTICLE_HEADER_V1, shared by RR7 atlas and atlas-astro.
//
// Request-context-agnostic design (per CLAUDE.md single-source rule):
//   - `isLoggedIn` replaces `myocData`: RR7 derives it from `!!myocData.myOCUser`; atlas-astro from
//     `genericConfig.isLoggedIn`.
//   - `imageWidth` replaces `deviceType`: RR7 glue resolves `isMobile` via `useMedia()` and passes
//     400 (mobile) or 1000 (desktop); atlas-astro glue uses `useDevice(deviceType)`.
//   - `onLogin` replaces `useAuthModal().openAtView`: atlas-astro uses the cross-island auth store
//     (NGF-6331); RR7 uses `useAuthModal().openAtView`. See dependency-cycle rule in CLAUDE.md.
//   - `publishedTimeLocalised` is the pre-formatted date string (repub timezone + locale). The caller
//     resolves `REPUBS[repub].dateProperties` and formats via `formatInTimeZone`; the shared component
//     no longer reads REPUBS itself, keeping it free of the repub-specific import in the hot island path.
export const ArticleHeader = ({
  aiGeneratedId,
  authors,
  componentTags,
  description,
  image,
  imageWidth,
  isLoggedIn,
  onLogin,
  publishedTimeLocalised,
  readTime,
  title,
}: ArticleHeaderProps) => {
  // Defensive: a misconfigured CMS payload may deliver an empty authors array. `author` is the first
  // author (or undefined); both the effect and the render guard on it. The effect is still called
  // unconditionally (rules-of-hooks) and short-circuits when there is no author — no sentinel object
  // is needed. `author` (a stable `authors[0]` reference) is the dep, NOT the `authors` array itself:
  // a fresh-but-equal array reference from the parent would otherwise re-fire the analytics event.
  const author = authors[0];

  const renderLoginBanner = componentTags?.includes('TIPS_PREMIUM');

  React.useEffect(() => {
    if (!author) return;
    trackEvent('article_view', {
      tipsterName: author.fullName,
      articleName: title,
      isLocked: renderLoginBanner ? 'yes' : 'no',
      aiArticle: aiGeneratedId ? 'yes' : 'no',
    });
  }, [author, aiGeneratedId, renderLoginBanner, title]);

  // If the CMS delivers no authors at all, render nothing and let the ErrorBoundary handle the slot.
  if (!author) return null;

  return (
    <header aria-labelledby="article-title" data-testid="article-header-container">
      <picture className={styles.articleHeroImage}>
        <img
          alt={image.alt}
          className="w-full h-full absolute object-cover object-top"
          data-testid="article-hero-image"
          src={getWebPImageUrlWithWidth(image.url, imageWidth)}
        />
      </picture>
      <h1 className={styles.articleHeader} data-testid="article-title">
        {title}
      </h1>
      <h2 className={styles.articleSummary} data-testid="article-subtitle">
        {description}
      </h2>

      {renderLoginBanner && (
        <div className={styles.lockedContentIcon}>
          {!isLoggedIn ? (
            <button
              className={styles.lockedContentButton}
              onClick={() => {
                trackEvent('article_header_login', {
                  tipsterName: author.fullName,
                  articleName: title,
                });
                onLogin();
              }}
              type="button"
            >
              log in to view for free
              <PremiumLock />
            </button>
          ) : (
            <>unlocked</>
          )}
        </div>
      )}
      <div className={styles.articleDetailsContainer}>
        <p className={styles.articleAuthor} data-testid="article-author-name">
          {author.fullName}
          {publishedTimeLocalised && ` - ${publishedTimeLocalised}`}
        </p>
        {readTime && (
          <span className={styles.articleMetadata} data-testid="article-metadata-read-time">
            {readTime}
          </span>
        )}
      </div>
    </header>
  );
};
